import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useIsMobile } from '../../../hooks';
import { MobileList } from '../../../components/common';
import api from '../../../services/api';
import { grey, primary, success, warning, error as errorColor, info } from '../../../styles/tokens';

/**
 * Liste des Coachs (Admin)
 */
const CoachList = () => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  // Filtres
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');

  // Menu actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCoach, setSelectedCoach] = useState(null);

  // Dialogs
  const [suspendDialog, setSuspendDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchCoaches = async () => {
    try {
      setLoading(true);
      setError(null);

      let url = `/admin/coaches?page=${pagination.page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (planFilter) url += `&plan=${planFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await api.get(url);

      setCoaches(response.coaches || []);
      setPagination(prev => ({
        ...prev,
        pages: response.pagination?.pages || 1,
        total: response.pagination?.total || 0,
      }));
    } catch (err) {
      console.error('Erreur lors du chargement des coachs:', err);
      setError('Impossible de charger la liste des coachs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, statusFilter, planFilter]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchCoaches();
    }
  };

  const handleMenuOpen = (event, coach) => {
    setAnchorEl(event.currentTarget);
    setSelectedCoach(coach);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSuspend = async () => {
    if (!selectedCoach) return;

    try {
      setActionLoading(true);
      await api.post(`/admin/coaches/${selectedCoach._id}/suspend`);
      setSuspendDialog(false);
      handleMenuClose();
      fetchCoaches();
    } catch (err) {
      console.error('Erreur lors de la suspension:', err);
      setError('Impossible de suspendre le coach.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!selectedCoach) return;

    try {
      setActionLoading(true);
      await api.post(`/admin/coaches/${selectedCoach._id}/reactivate`);
      handleMenuClose();
      fetchCoaches();
    } catch (err) {
      console.error('Erreur lors de la reactivation:', err);
      setError('Impossible de reactiver le coach.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCoach) return;

    try {
      setActionLoading(true);
      await api.delete(`/admin/coaches/${selectedCoach._id}`);
      setDeleteDialog(false);
      handleMenuClose();
      fetchCoaches();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Impossible de supprimer le coach.');
    } finally {
      setActionLoading(false);
    }
  };

  const getPlanChipProps = (plan) => {
    const configs = {
      starter: { label: 'starter', sx: { bgcolor: grey[100], color: grey[700] } },
      pro: { label: 'pro', sx: { bgcolor: primary[50], color: primary[600] } },
      enterprise: { label: 'enterprise', sx: { bgcolor: success[50], color: success.dark } },
    };
    return configs[plan] || { label: plan || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  const getStatusChipProps = (status) => {
    const configs = {
      active: { label: 'active', sx: { bgcolor: success[50], color: success.dark } },
      suspended: { label: 'suspended', sx: { bgcolor: errorColor[50], color: errorColor.dark } },
      pending: { label: 'pending', sx: { bgcolor: warning[50], color: warning.dark } },
      provisioning: { label: 'provisioning', sx: { bgcolor: info[50], color: info.dark } },
    };
    return configs[status] || { label: status || 'N/A', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  return (
    <>
      <Helmet>
        <title>Gestion des coachs - Admin Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h1">Coachs</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            component={Link}
            to="/admin/coaches/new"
          >
            Ajouter
          </Button>
        </Stack>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                placeholder="Rechercher par nom ou email..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={handleSearch}
                sx={{ flexGrow: 1, maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: grey[400] }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Statut</InputLabel>
                <Select
                  value={statusFilter}
                  label="Statut"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="active">Actif</MenuItem>
                  <MenuItem value="suspended">Suspendu</MenuItem>
                  <MenuItem value="pending">En attente</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Plan</InputLabel>
                <Select
                  value={planFilter}
                  label="Plan"
                  onChange={(e) => {
                    setPlanFilter(e.target.value);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                >
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="starter">Starter</MenuItem>
                  <MenuItem value="pro">Pro</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* Erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Liste */}
        <Card className={isMobile ? 'mobile-list-container' : ''}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : coaches.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2">
                Aucun coach trouve.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile: Liste en cards */
            <Box sx={{ p: 2 }}>
              <MobileList
                items={coaches}
                renderItem={(coach) => {
                  const planProps = getPlanChipProps(coach.subscriptionPlan);
                  const statusProps = getStatusChipProps(coach.tenantStatus);
                  return {
                    primary: `${coach.firstName} ${coach.lastName}`,
                    secondary: coach.email,
                    tertiary: `${coach.currentClients || 0} clients`,
                    meta: new Date(coach.createdAt).toLocaleDateString('fr-FR'),
                    chips: [
                      { label: planProps.label, sx: planProps.sx },
                      { label: statusProps.label, sx: statusProps.sx },
                    ],
                    onClick: () => navigate(`/admin/coaches/${coach._id}`),
                  };
                }}
                emptyMessage="Aucun coach trouve"
              />
            </Box>
          ) : (
            /* Desktop: Table classique */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Coach</TableCell>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Clients</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Inscription</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {coaches.map((coach) => {
                    const planProps = getPlanChipProps(coach.subscriptionPlan);
                    const statusProps = getStatusChipProps(coach.tenantStatus);
                    return (
                      <TableRow key={coach._id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {coach.firstName} {coach.lastName}
                            </Typography>
                            <Typography variant="caption">
                              {coach.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            sx={{ fontFamily: 'monospace', color: grey[500] }}
                          >
                            {coach.tenantId || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={planProps.label}
                            size="small"
                            sx={planProps.sx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {coach.currentClients || 0} / {coach.maxClients || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusProps.label}
                            size="small"
                            sx={statusProps.sx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1">
                            {new Date(coach.createdAt).toLocaleDateString('fr-FR')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, coach)}
                          >
                            <MoreVertIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={(e, page) => setPagination(prev => ({ ...prev, page }))}
              color="primary"
            />
          </Box>
        )}

        {/* Menu actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            component={Link}
            to={`/admin/coaches/${selectedCoach?._id}`}
            onClick={handleMenuClose}
          >
            <VisibilityIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Voir details
          </MenuItem>

          <MenuItem
            component={Link}
            to={`/admin/coaches/${selectedCoach?._id}/edit`}
            onClick={handleMenuClose}
          >
            <EditIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Modifier
          </MenuItem>

          {selectedCoach?.tenantStatus === 'active' ? (
            <MenuItem onClick={() => setSuspendDialog(true)}>
              <BlockIcon sx={{ fontSize: 18, mr: 1.5 }} />
              Suspendre
            </MenuItem>
          ) : (
            <MenuItem onClick={handleReactivate} disabled={actionLoading}>
              <PlayArrowIcon sx={{ fontSize: 18, mr: 1.5 }} />
              Reactiver
            </MenuItem>
          )}

          <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Supprimer
          </MenuItem>
        </Menu>

        {/* Dialog Suspension */}
        <Dialog open={suspendDialog} onClose={() => setSuspendDialog(false)}>
          <DialogTitle>Suspendre le coach ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir suspendre <strong>{selectedCoach?.firstName} {selectedCoach?.lastName}</strong> ?
              <br /><br />
              Le coach ne pourra plus acceder a son espace et ses clients ne pourront plus consulter leurs programmes.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSuspendDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleSuspend} color="warning" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Suspendre'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Suppression */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Supprimer le coach ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir supprimer definitivement <strong>{selectedCoach?.firstName} {selectedCoach?.lastName}</strong> ?
              <br /><br />
              <strong>Cette action est irreversible.</strong> Toutes les donnees du coach et de ses clients seront perdues.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CoachList;
